import dayjs from 'dayjs';
// Importing OPTISIGNS_TZ also GUARANTEES the dayjs utc + timezone plugins have been extended:
// format-date.ts does that at module scope, and `.tz()` below throws without them. Do not
// re-extend here, and do not swap this for a bare string constant.
import { OPTISIGNS_TZ } from '@/lib/format-date';

/**
 * OptiClock month + day helpers — the client half of the bare-date invariant.
 *
 * Two dates in this feature are computed on the CLIENT, and both must be computed in ONE
 * canonical timezone rather than the browser's:
 *
 *   - what "today" defaults to in the entry form
 *   - which month the pickers open on
 *
 * The CS team spans Vietnam and US Central. Without a canonical zone, two agents disagree about
 * what month it is right now, and — worse — an agent logging their evening on the last day of
 * the month files it into the NEXT month and watches their own total drop to zero.
 *
 * `new Date().toISOString().slice(0, 10)` is the specific bug being avoided: at 21:00 Central
 * on Aug 31 it returns '2026-09-01'. format-date.ts:90-98 documents the same hazard on the
 * output side.
 */

/** Today in Central, as `YYYY-MM-DD`. */
export function todayCentral(): string {
  return dayjs().tz(OPTISIGNS_TZ).format('YYYY-MM-DD');
}

/** The current month in Central, as `YYYY-MM`. */
export function currentMonthCentral(): string {
  return dayjs().tz(OPTISIGNS_TZ).format('YYYY-MM');
}

/**
 * The current month plus the previous 11, newest first, as `YYYY-MM` — exactly the wire
 * format, so nothing converts between the picker and the query.
 *
 * Bounded and backward-only on purpose. There is no Clockify backfill, so months before
 * go-live are always empty: an unbounded picker lets an operator page back to 2019 and
 * conclude the feature is broken. It also structurally removes "next month", which cannot
 * contain anything.
 */
export function monthOptions(count = 12): string[] {
  const now = dayjs().tz(OPTISIGNS_TZ);
  return Array.from({ length: count }, (_, i) => now.subtract(i, 'month').format('YYYY-MM'));
}

/** `2026-08` → `August 2026`. */
export function monthLabel(month: string): string {
  const d = dayjs(`${month}-01`, 'YYYY-MM-DD');
  return d.isValid() ? d.format('MMMM YYYY') : month;
}
